import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { Columns3, LucideAngularModule, X } from 'lucide-angular';
import { Columns } from '@shared/interfaces/table/column-config';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * TableColumnSelectorComponent provides a UI for selecting which columns to display in a table.
 *
 * This component allows users to toggle visibility of table columns from a dropdown selector.
 * It maintains a two-way binding with its parent component to track selected columns.
 *
 * @template T - The type of data displayed in the table rows
 */
@Component({
  selector: 'app-table-column-selector',
  imports: [
    LucideAngularModule,
    TranslatePipe,
  ],
  templateUrl: './table-column-selector.component.html',
  styleUrl: './table-column-selector.component.css'
})
export class TableColumnSelectorComponent<T> {
  /** Column Selection Two-way binding **/
  @Input() public selectedColumns: Columns<T>[] = [];
  @Output() public selectedColumnsChange: EventEmitter<Columns<T>[]> = new EventEmitter();

  /**
   * Close the dropdown when clicking outside of it
   * @param event
   */
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    if (event instanceof MouseEvent) {
      if (this.selectorOpen && !this.elementRef.nativeElement.contains(event.target)) {
        this.selectorOpen = false;
      }
    }
  }

  /**
   * The complete list of columns available for selection
   */
  @Input() public availableColumns: Columns<T>[] = [];

  /**
   * Event emitter for resetting column settings back to default
   */
  @Output() public resetSettings: EventEmitter<boolean> = new EventEmitter();

  /** Icons **/
  protected readonly Columns3 = Columns3;
  protected readonly X = X;

  /**
   * Controls the visibility of the column selector dropdown
   */
  public selectorOpen: boolean = false;


  constructor(
    private readonly elementRef: ElementRef,
  ) { }

  /**
   * Toggles the visibility of the column selector dropdown
   */
  public toggle(): void {
    this.selectorOpen = !this.selectorOpen;
  }

  /**
   * Toggles a column's selection status
   *
   * If the column is already selected, it will be removed from the selection.
   * If the column is not selected, it will be added to the selection.
   *
   * @param column - The column to toggle selection for
   */
  public toggleColumn(column: Columns<T>): void {
    if (this.selectedColumns.indexOf(column) > -1) {
      this.selectedColumns.splice(this.selectedColumns.indexOf(column), 1);
    } else {
      this.selectedColumns.push(column);
    }

    this.selectedColumnsChange.emit(this.selectedColumns);
  }

  /**
   * Triggers a reset of column settings to default values
   * Emits the resetSettings event to notify parent components
   */
  public reset(): void {
    this.resetSettings.emit(true);
  }


}
